import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCompany } from "./useCompany";
import { getDevPlanOverride } from "./useDevPlanOverride";

export type SubscriptionTier = "free" | "pro" | "scale" | "fleet";

export const VEHICLE_LIMITS: Record<SubscriptionTier, number | null> = {
  free: 1,
  pro: 5,
  scale: 15,
  fleet: null, // unlimited
};

export function getVehicleLimit(tier: SubscriptionTier): number | null {
  return VEHICLE_LIMITS[tier];
}

interface SubscriptionStatus {
  tier: SubscriptionTier;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
}

export function useSubscription() {
  const { company } = useCompany();

  const { data: subscriptionData, isLoading, refetch } = useQuery({
    queryKey: ["subscription", company?.id],
    queryFn: async (): Promise<SubscriptionStatus> => {
      if (!company?.id) {
        return { tier: "free", cancelAtPeriodEnd: false, currentPeriodEnd: null };
      }
      
      const tier = (company.subscription_tier as SubscriptionTier) || "free";
      
      // For paid tiers, check Stripe for cancellation status
      if (tier !== "free") {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const response = await supabase.functions.invoke("check-subscription-status");
            if (response.data && !response.error) {
              return {
                tier,
                cancelAtPeriodEnd: response.data.cancelAtPeriodEnd || false,
                currentPeriodEnd: response.data.currentPeriodEnd || null,
              };
            }
          }
        } catch (error) {
          console.error("Failed to check subscription status:", error);
        }
      }
      
      return { tier, cancelAtPeriodEnd: false, currentPeriodEnd: null };
    },
    enabled: !!company?.id,
  });

  const devOverride = getDevPlanOverride();
  const subscriptionTier = devOverride || subscriptionData?.tier || "free";
  const cancelAtPeriodEnd = subscriptionData?.cancelAtPeriodEnd || false;
  const currentPeriodEnd = subscriptionData?.currentPeriodEnd || null;

  const createCheckoutSession = async (tier: SubscriptionTier, billingInterval: "monthly" | "annual" = "monthly") => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("You must be logged in to subscribe");
    }

    const response = await supabase.functions.invoke("create-checkout", {
      body: { 
        tier, 
        billingInterval,
        returnUrl: window.location.origin 
      },
    });

    if (response.error) {
      throw new Error(response.error.message || "Failed to create checkout session");
    }

    if (response.data?.url) {
      window.location.href = response.data.url;
    } else {
      throw new Error("No checkout URL received");
    }
  };

  const openCustomerPortal = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("You must be logged in to manage billing");
    }

    const response = await supabase.functions.invoke("create-portal-session", {
      body: { 
        returnUrl: `${window.location.origin}/app/settings` 
      },
    });

    if (response.error) {
      throw new Error(response.error.message || "Failed to open billing portal");
    }

    if (response.data?.url) {
      window.location.href = response.data.url;
    } else {
      throw new Error("No portal URL received");
    }
  };

  const upgradeSubscription = async (tier: SubscriptionTier, billingInterval: "monthly" | "annual" = "monthly") => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("You must be logged in to upgrade");
    }

    const response = await supabase.functions.invoke("upgrade-subscription", {
      body: { tier, billingInterval },
    });

    if (response.error) {
      throw new Error(response.error.message || "Failed to upgrade subscription");
    }

    if (response.data?.error) {
      throw new Error(response.data.error);
    }

    return response.data;
  };

  const reactivateSubscription = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error("You must be logged in to reactivate");
    }

    // Call upgrade-subscription with the current tier to trigger re-activation
    const response = await supabase.functions.invoke("upgrade-subscription", {
      body: { tier: subscriptionTier, billingInterval: "monthly" },
    });

    if (response.error) {
      throw new Error(response.error.message || "Failed to reactivate subscription");
    }

    if (response.data?.error) {
      throw new Error(response.data.error);
    }

    return response.data;
  };

  const vehicleLimit = getVehicleLimit(subscriptionTier);

  return {
    subscriptionTier,
    cancelAtPeriodEnd,
    currentPeriodEnd,
    isLoading,
    isProOrAbove: subscriptionTier === "pro" || subscriptionTier === "scale" || subscriptionTier === "fleet",
    isScaleOrAbove: subscriptionTier === "scale" || subscriptionTier === "fleet",
    isFleetOrAbove: subscriptionTier === "fleet",
    vehicleLimit,
    createCheckoutSession,
    openCustomerPortal,
    upgradeSubscription,
    reactivateSubscription,
    refetch,
  };
}
